import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Clock, Eye, Send, Shield, Pin, Lock, ThumbsUp, CornerDownRight } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const CAT_CONFIG = {
  'general': { label: 'Général', color: '#64748b', bg: '#f1f5f9' },
  'cas-cliniques': { label: 'Cas Cliniques', color: '#2563eb', bg: '#eff6ff' },
  'lentilles': { label: 'Lentilles', color: '#059669', bg: '#ecfdf5' },
  'équipement': { label: 'Équipement', color: '#d97706', bg: '#fffbeb' },
  'ortho-k': { label: 'Orthokératologie', color: '#7c3aed', bg: '#f5f3ff' },
  'pediatrie': { label: 'Pédiatrie', color: '#e11d48', bg: '#fff1f2' },
  'autre': { label: 'Autre', color: '#0891b2', bg: '#ecfeff' },
};

const AVATAR_GRADIENTS = [
  'from-[#1e3a5f] to-[#35AFE4]',
  'from-emerald-600 to-teal-500',
  'from-violet-600 to-purple-500',
  'from-rose-500 to-pink-500',
  'from-amber-500 to-orange-500',
  'from-cyan-500 to-blue-500',
];

const getGradient = (name) => {
  const hash = (name || '?').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const timeAgo = (dateStr) => {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} jours`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ForumTopicPage = () => {
  const navigate = useNavigate();
  const { topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const hasAccess = () => {
    if (!user || !token) return false;
    const role = user.role;
    if (['super_admin', 'admin', 'regional', 'auteur', 'tresorier'].includes(role)) return true;
    if (role === 'ophthalmologist' && user.subscription_status === 'active') return true;
    if (role === 'intern' && user.approval_status === 'approved') return true;
    return false;
  };

  useEffect(() => {
    if (!hasAccess()) {
      setLoading(false);
      setAccessDenied(true);
      return;
    }
    fetchTopic();
  }, [topicId]);

  const fetchTopic = async () => {
    try {
      const res = await axios.get(`${API}/forum/topics/${topicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopic(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Sujet introuvable');
        navigate('/forum');
      }
    }
    setLoading(false);
  };

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/topics/${topicId}/replies`, { content: replyContent }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Réponse soumise ! Visible après modération.');
      setReplyContent('');
      fetchTopic();
    } catch { toast.error('Erreur'); }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#f8fafb] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-[2.5px] border-[#35AFE4] border-t-transparent rounded-full animate-spin" />
        <p className="text-xs text-gray-400">Chargement...</p>
      </div>
    </div>
  );

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="forum-topic-page">
        <SiteHeader />
        <div className="flex items-center justify-center px-4" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#1e3a5f] to-[#35AFE4] flex items-center justify-center shadow-lg shadow-blue-900/20">
              <Lock size={32} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1e3a5f] mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Accès réservé</h1>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">Ce sujet est reserve aux membres adhérents de la SFOALC.</p>
            <button onClick={() => navigate('/auth')} className="px-6 py-3 bg-[#35AFE4] text-white text-sm font-bold rounded-xl hover:bg-[#1e3a5f] transition-all" data-testid="login-cta">Se connecter</button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!topic) return null;

  const cat = CAT_CONFIG[topic.category] || CAT_CONFIG.general;

  return (
    <div className="min-h-screen bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="forum-topic-page">
      <SiteHeader />
      <SEO title={topic.title} />

      {/* Breadcrumb bar */}
      <div className="border-b border-gray-100 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2 text-[11px] text-gray-400">
          <button onClick={() => navigate('/forum')} className="hover:text-[#35AFE4] transition-colors font-medium" data-testid="back-btn">Forum</button>
          <span>/</span>
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold" style={{ background: cat.bg, color: cat.color }}>{cat.label}</span>
          <span>/</span>
          <span className="text-gray-600 font-medium truncate max-w-[300px]">{topic.title}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* ===== TOPIC ===== */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-8" data-testid="topic-detail">
          {/* Topic header */}
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getGradient(topic.author_name)} flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm`}>
                {topic.author_name?.slice(0, 2).toUpperCase() || '??'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  {topic.pinned && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 rounded-md text-[9px] font-bold border border-amber-100">
                      <Pin size={9} className="-rotate-45" /> Épinglé
                    </span>
                  )}
                  <span className="px-2.5 py-0.5 rounded-md text-[10px] font-bold" style={{ background: cat.bg, color: cat.color }}>
                    {cat.label}
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] leading-tight mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  {topic.title}
                </h1>
                <div className="flex items-center gap-4 text-[11px] text-gray-400 mb-6 flex-wrap">
                  <span className="font-semibold text-gray-600">{topic.author_name}</span>
                  <span className="flex items-center gap-1"><Clock size={10} /> {timeAgo(topic.created_at)}</span>
                  <span className="flex items-center gap-1"><Eye size={10} /> {topic.views} vues</span>
                  <span className="flex items-center gap-1"><MessageSquare size={10} /> {topic.replies?.length || 0} réponses</span>
                </div>
                <div className="text-sm text-gray-600 leading-[1.8] whitespace-pre-wrap" data-testid="topic-content">
                  {topic.content}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== REPLIES ===== */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-bold text-[#1e3a5f] flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
              <MessageSquare size={15} className="text-[#35AFE4]" />
              {topic.replies?.length || 0} réponse{(topic.replies?.length || 0) > 1 ? 's' : ''}
            </h3>
          </div>

          {topic.replies?.length > 0 ? (
            <div className="space-y-3">
              {topic.replies.map((r, i) => (
                <div key={r.id} className="flex gap-3 sm:gap-4" data-testid={`reply-${i}`}>
                  {/* Thread line */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${getGradient(r.author_name)} flex items-center justify-center text-white text-[10px] font-bold shadow-sm`}>
                      {r.author_name?.slice(0, 2).toUpperCase() || '??'}
                    </div>
                    {i < topic.replies.length - 1 && (
                      <div className="w-px flex-1 bg-gray-200 mt-2" />
                    )}
                  </div>

                  {/* Reply content */}
                  <div className="flex-1 bg-white rounded-xl border border-gray-100 p-4 sm:p-5 mb-1 hover:shadow-sm transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-800">{r.author_name}</span>
                        <span className="text-[10px] text-gray-300">{timeAgo(r.created_at)}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-[1.75] whitespace-pre-wrap">{r.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
              <CornerDownRight size={28} className="text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Pas encore de réponse</p>
              <p className="text-[11px] text-gray-300 mt-1">Soyez le premier à répondre !</p>
            </div>
          )}
        </div>

        {/* ===== REPLY FORM ===== */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="reply-form">
          <div className="px-5 py-3 border-b border-gray-50 bg-gray-50/50">
            <p className="text-[11px] font-semibold text-gray-500 flex items-center gap-1.5">
              <CornerDownRight size={11} className="text-[#35AFE4]" /> Ajouter une réponse
            </p>
          </div>
          <div className="p-5">
            <div className="flex gap-3">
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${getGradient(user?.first_name)} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 shadow-sm`}>
                {user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : '??'}
              </div>
              <div className="flex-1">
                <textarea value={replyContent} onChange={e => setReplyContent(e.target.value)}
                  placeholder="Partagez votre expérience ou répondez à la question..."
                  rows={4}
                  className="w-full px-4 py-3 bg-[#f8fafb] border border-gray-200 rounded-xl text-sm resize-none outline-none focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] focus:bg-white transition-all"
                  data-testid="reply-input" />
                <div className="flex items-center justify-between mt-3">
                  <p className="text-[10px] text-gray-400 flex items-center gap-1"><Shield size={10} className="text-[#35AFE4]" /> Soumise à modération</p>
                  <button onClick={handleReply} disabled={submitting || !replyContent.trim()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#35AFE4] text-white text-xs font-bold rounded-xl hover:bg-[#1e3a5f] disabled:opacity-40 transition-all shadow-sm shadow-blue-900/10"
                    data-testid="submit-reply-btn">
                    <Send size={13} /> {submitting ? 'Envoi...' : 'Repondre'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForumTopicPage;
